import { Injectable } from "@angular/core";
import { MutexService } from "common/concurrency/mutex.service";
import { BroadcastService } from "core/broadcast.service";
import { Events } from "core/events";
import { UserResource } from "core/stages-client";

type UserSettings = stages.user.UserSettings;
type UserSettingsMetadata = stages.user.UserSettingsMetadata;
type Upload = stages.file.Upload;

@Injectable({ providedIn: "root" })
export class SettingsService {
	constructor(
		private mutexService: MutexService,
		private broadcastService: BroadcastService,
		private readonly userResource: UserResource,
	) {}

	async getSettings(currentWorkspaceId: string, pv: string): Promise<DataAndInfo<UserSettings, UserSettingsMetadata>> {
		this.userResource.setAdditionalHttpParamsForNextRequest({
			workspaceId: currentWorkspaceId,
			pv: pv,
		});
		return this.userResource.getSettings();
	}

	async putSettings(userSettings: UserSettings, currentWorkspaceId: string, pv: string): Promise<void> {
		this.userResource.setAdditionalHttpParamsForNextRequest({
			workspaceId: currentWorkspaceId,
			pv: pv,
		});
		await this.userResource.saveSettings(userSettings);
	}

	async changePassword(
		oldPassword: string,
		newPassword: string,
		securityCode: string | null,
		currentWorkspaceId: string,
		pv: string,
	): Promise<void> {
		this.userResource.setAdditionalHttpParamsForNextRequest({
			workspaceId: currentWorkspaceId,
			pv: pv,
		});
		return this.mutexService.invokeWithResult("mutexChangePassword", async () => {
			await this.userResource.changePassword({
				oldPassword: oldPassword,
				newPassword: newPassword,
				securityCode: securityCode,
			});
			this.broadcastService.broadcast(Events.MESSAGE, {
				translate: "user.password.changed",
			});
		});
	}

	async saveProfileImage(
		profileImageUpload: Upload,
		cropPoints: number[],
		currentWorkspaceId: string,
		pv: string,
	): Promise<stages.core.format.User> {
		return this.mutexService.invokeWithResult("saveProfileImage", async () => {
			this.userResource.setAdditionalHttpParamsForNextRequest({
				workspaceId: currentWorkspaceId,
				pv: pv,
			});
			return this.userResource.cropProfileImage({
				profileImageUpload: profileImageUpload,
				cropPoints: cropPoints,
			});
		});
	}

	async cancelPreview(uploadId: string, currentWorkspaceId: string, pv: string): Promise<void> {
		this.userResource.setAdditionalHttpParamsForNextRequest({
			workspaceId: currentWorkspaceId,
			pv: pv,
		});
		return this.userResource.cancelPreview(uploadId);
	}
}
